import { AxiosError } from 'axios';
import { QueryCache, QueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

// Extract an HTTP status code from any error shape. Returns 0 for a network /
// non-HTTP error (no response), which we treat as a transient (retryable) class.
function httpStatusOf(error: unknown): number {
  return Number((error as AxiosError)?.response?.status) || 0;
}

function queryErrorHandler(error: unknown) {
  // error is type unknown because in js, anything can be an error (e.g. throw(5))
  // const title =
  //   error instanceof Error ? error.message : 'error connecting to server';

  const status = httpStatusOf(error);
  // Only capture genuine failures: server errors (>=500) and network/non-HTTP
  // errors (status 0). Expected client errors (400/401/403/404) are routine
  // control-flow outcomes (auth checks, optional-resource probes) and were the
  // dominant source of Sentry noise — drop them (RC-8 Q14–Q20).
  if (status === 0 || status >= 500) {
    Sentry.captureException(error, {
      extra: {
        stack: error instanceof Error ? error.stack : undefined,
      },
    });
  }
}

// Global retry policy (RC-8 Q14–Q20). RC-1 deliberately deferred setting a
// global retry so the policy decision lived in one place here:
//   - Never retry 4xx: a 400/401/403/404 is deterministic, so retrying only
//     amplifies it (the 404 "storm" RC-1 flagged) without ever succeeding.
//   - Retry 5xx and network/non-HTTP errors up to 2 extra attempts (3 total),
//     which are the transient classes a retry can actually recover from.
// Per-hook `retry` overrides (e.g. the `retry: 0` on auth/users hooks) still
// win — react-query uses the query-level option when present.
function defaultRetry(failureCount: number, error: unknown): boolean {
  const status = httpStatusOf(error);
  if (status >= 400 && status < 500) {
    return false;
  }
  return failureCount < 2;
}

export function generateQueryClient(): QueryClient {
  return new QueryClient({
    // Query capture lives on the cache: `defaultOptions.queries.onError` is
    // removed in react-query 5. Mutations keep `defaultOptions.mutations.onError`,
    // which v5 still supports.
    queryCache: new QueryCache({ onError: queryErrorHandler }),
    defaultOptions: {
      queries: {
        retry: defaultRetry,
        // 30s — the value companies.ts already converged on (de-facto house
        // convention). Suppresses sub-30s remount refetch storms.
        staleTime: 30_000,
        // Equals the library default; made explicit for review.
        gcTime: 5 * 60_000,
        // The focus-storm trigger; the 3 source sites that want true keep an
        // explicit override.
        refetchOnWindowFocus: false,
        // No source hook wants this true; avoids reconnect/tab-restore storms.
        refetchOnReconnect: false,
      },
      mutations: {
        onError: queryErrorHandler,
      },
    },
  });
}

export const queryClient = generateQueryClient();
